import PropTypes from 'prop-types';
import React from 'react';
import Button from 'relements/build/esm/ui/button';
import Tabs from 'relements/build/esm/ui/tabs';

// Stores
import ShareBot from '@src/components/BotModals/ShareBot';
import { openComponentModal } from '@src/actions/BotActions';
import { getAllPlatforms } from '@src/api/business';

// Local Components
import Tasks from './Tasks';
import BotbreakMessage from './BotBreakMessage';
import OutlierMessage from './OutlierMessage';
import SDKConfigurations from './SDKConfigurations';
import PlatformSettings from './PlatformSettings';

import './PartnerSpecific.scss';
import { DEFAULTS } from '../../const';

export default class PartnerSpecific extends React.Component {
  TAB_PATHS = {
    SDK_CONFIG: 'sdk-configurations',
    TASKBOX: 'taskbox',
    BOTBREAK: 'bot-break-messages',
    OUTLIER: 'outlier-messages',
    DEPLOYMENTS: 'deployments',
  };

  TABS = [
    {
      title: 'SDK Configurations',
      path: this.TAB_PATHS.SDK_CONFIG,
    },
    {
      title: 'Menu',
      path: this.TAB_PATHS.TASKBOX,
    },
    {
      title: 'Bot Break Messages',
      path: this.TAB_PATHS.BOTBREAK,
    },
    {
      title: 'Outlier Messages',
      path: this.TAB_PATHS.OUTLIER,
    },
    {
      title: 'Platform Deployments',
      path: this.TAB_PATHS.DEPLOYMENTS,
      disabled: this.props.meta.isNew,
    },
  ];

  constructor(props) {
    super(props);
    const initialPartner =
      props.value && props.meta.activePartners && props.meta.activePartners[0];
    this.state = {
      currentPartner: initialPartner,
      currentTab: this.TAB_PATHS.SDK_CONFIG,
      allPlatforms: [],
    };
  }

  componentDidMount() {
    getAllPlatforms().then((platforms) => {
      const allPlatforms = [
        {
          title: 'All',
          value: 'default',
        },
      ].concat(
        platforms.body.map((platform) => ({
          title: platform.title,
          value: platform.id,
        }))
      );

      this.setState({ allPlatforms });
    });
  }

  componentWillReceiveProps(nextProps) {
    if (!this.state.currentPartner && this.props.meta !== nextProps.meta) {
      this.setState({ currentPartner: nextProps.meta.activePartners[0] });
    }
  }

  render() {
    return (
      <div className="business-settings partner-specific-settings">
        {this.renderHeader()}
        {this.renderPartnerSelect()}
        {this.renderTabs()}
      </div>
    );
  }

  renderHeader() {
    return (
      <div className="business-settings-header">
        <div className="business-settings-title">Account Specific Settings</div>
      </div>
    );
  }

  renderPartnerSelect() {
    const { activePartners = [] } = this.props.meta;
    if (activePartners.length === 0)
      return (
        <span className="business-settings-zero-state">
          <strong>No accounts added to this business.</strong> 
          {' '}
          <br />
          {' '}
          Add an
          account through the general settings to edit account specific settings
        </span>
      );
    return (
      <div className="business-settings-partner-select">
        <div className="business-settings-partner-wrapper">
          <div className="business-settings-partner-heading">Account:</div>
          <div className="business-settings-partner-selected">
            {this.state.currentPartner}
          </div>
        </div>
        <div className="business-settings-ctas">
          <Button secondary onClick={this.viewCredentials}>
            View Credentials
          </Button>
        </div>
      </div>
    );
  }

  renderTabs() {
    if (!this.state.currentPartner) return null;
    return (
      <div className="business-properties-body">
        <Tabs
          value={this.state.currentTab}
          className="business-properties-body-tabs"
        >
          {this.TABS.map((tab) => (
            <Tabs.Item
              onClick={(currentTab) => this.setState({ currentTab })}
              key={tab.path}
              value={tab.path}
            >
              {tab.title}
            </Tabs.Item>
          ))}
        </Tabs>
        {this.renderTab()}
      </div>
    );
  }

  renderTab() {
    const {
      settingsIndexed = {},
      allTasks,
      taskboxMessage,
      callbackBaseUrl,
    } = this.props.value;
    const currentPartnerData =
      settingsIndexed[this.state.currentPartner] || DEFAULTS.PARTNER_SETTINGS;
    switch (this.state.currentTab) {
      case this.TAB_PATHS.TASKBOX:
        return (
          <Tasks
            value={currentPartnerData.taskboxSettings}
            meta={{ allTasks, taskboxMessage, ...this.props.meta }}
            onChange={this.handleOnChange('taskboxSettings')}
            onTopLevelChange={this.handleOnTopLevelChange}
            onTransfer={this.props.onTransferTask}
          />
        );
      case this.TAB_PATHS.BOTBREAK:
        return (
          <BotbreakMessage
            value={currentPartnerData.botBreakMessage}
            meta={this.props.meta}
            onChange={this.handleOnChange('botBreakMessage')}
            allPlatforms={this.state.allPlatforms}
          />
        );
      case this.TAB_PATHS.OUTLIER:
        return (
          <OutlierMessage
            value={currentPartnerData.outlierMessage}
            meta={this.props.meta}
            onChange={this.handleOnChange('outlierMessage')}
            allPlatforms={this.state.allPlatforms}
          />
        );
      case this.TAB_PATHS.SDK_CONFIG:
        return (
          <SDKConfigurations
            value={currentPartnerData.sdkConfiguration}
            meta={this.props.meta}
            onChange={this.handleOnChange('sdkConfiguration')}
          />
        );
      case this.TAB_PATHS.DEPLOYMENTS:
        return (
          <PlatformSettings
            value={currentPartnerData.platformDeployments}
            callbackBaseUrl={callbackBaseUrl}
            meta={{
              currentPartner: this.state.currentPartner,
              ...this.props.meta,
            }}
            onChange={this.handleOnChange('platformDeployments')}
          />
        );
      default:
        return null;
    }
  }

  onActivePartnerChanged = (partner) => {
    this.setState({
      currentPartner: partner.value,
    });
  };

  handleOnChange = (key) => (keyValue) => {
    const { currentPartner } = this.state;
    const { value } = this.props;
    const { settingsIndexed = {} } = value;
    let currentSettings = settingsIndexed[currentPartner];
    if (!currentSettings) currentSettings = DEFAULTS.PARTNER_SETTINGS;
    this.props.onChange({
      ...value,
      settingsIndexed: {
        ...settingsIndexed,
        [currentPartner]: {
          ...currentSettings,
          [key]: keyValue,
        },
      },
    });
  };

  handleOnTopLevelChange = (key, keyValue) => {
    this.props.onChange({
      ...this.props.value,
      [key]: keyValue,
    });
  };

  viewCredentials = () => {
    const reverseMap = this.props.meta.reversePartnerIdMap;
    openComponentModal(
      <ShareBot
        businessId={this.props.meta.businessId}
        partner={reverseMap[this.state.currentPartner]}
      />,
      'Test Credentials',
      null,
      'close'
    );
  };
}

PartnerSpecific.propTypes = {
  meta: PropTypes.object,
  value: PropTypes.object,
  onChange: PropTypes.func,
  onTransferTask: PropTypes.func,
};

PartnerSpecific.defaultProps = {
  meta: {},
  value: {},
  onChange: () => {},
  onTransferTask: () => {},
};
